import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Copy, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { backofficeApi } from '@/lib/api-client'

interface ClientBankAccountNumberProps {
  account: { id: string; account_number_masked: string }
}

export function ClientBankAccountNumber({ account }: ClientBankAccountNumberProps) {
  const [revealed, setRevealed] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const reveal = useMutation({
    mutationFn: () => backofficeApi.revealClientBankAccount(account.id),
    onSuccess: (data) => setRevealed(data.account_number),
  })

  function copy() {
    if (!revealed) return
    void navigator.clipboard.writeText(revealed)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (revealed) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="font-mono select-none"
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
        >
          {revealed}
        </span>
        <Button type="button" variant="ghost" size="sm" className="size-6 p-0 max-sm:size-11" aria-label="Copy account number" onClick={copy}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
        {copied ? <span className="text-xs text-muted-foreground">Copied</span> : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-6 p-0 max-sm:size-11"
          aria-label="Hide account number"
          onClick={() => setRevealed(null)}
        >
          <EyeOff className="h-3.5 w-3.5" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="font-mono">{account.account_number_masked}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="size-6 p-0 max-sm:size-11"
        aria-label="Reveal account number"
        disabled={reveal.isPending}
        onClick={() => reveal.mutate()}
      >
        <Eye className="h-3.5 w-3.5" />
      </Button>
      {reveal.isError ? <span className="text-xs text-destructive">Reveal failed</span> : null}
    </div>
  )
}
